import {
  CalendarIcon,
  ChatAlt2Icon,
  CursorClickIcon,
  ExclamationCircleIcon,
  EyeIcon,
  GiftIcon,
  GlobeAltIcon,
  IdentificationIcon,
  LightningBoltIcon,
  MicrophoneIcon,
  RefreshIcon,
} from '@heroicons/react/outline';
import { FaWix } from 'react-icons/fa6';

import { useWebsiteFaqs } from './websiteShared';
import CodePanel from '../mocks/CodePanel';
import LeadCapture from '../mocks/LeadCapture';
import SiteChatDemo, { ChatButton } from '../mocks/SiteChatDemo';
import type { ChannelContent, DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';

const BRAND: DemoBrand = {
  name: 'Bloom & Stem',
  initial: 'B',
  bg: 'bg-[#e11d48]',
};

/*
 * Facts this page relies on (checked 2026-10-21,
 * docs/agents/website-platforms.mdx#wix, leads.mdx): the snippet goes into
 * Settings → Custom code (All pages, Load code once, Body - end); without
 * Custom code on the Wix plan, an Embed HTML element with the iframe; the
 * lead form appears after the first answer and, with its email alerts, is a
 * paid feature; Collect Data can also save to Leads.
 */
export function useWixContent(): ChannelContent {
  const { t } = useChannelsT();
  const websiteFaqs = useWebsiteFaqs();

  return {
    key: 'wix',
    name: 'Wix',
    logo: 'FaWix',
    seoTitle: t('Wix AI Chatbot: Add an AI Chat to Your Wix Site in Minutes'),
    seoDescription: t(
      'Add an AI chatbot to your Wix site with one snippet in Custom code. It answers visitors from your own content, captures leads and books appointments, 24/7.',
    ),
    hero: {
      label: t('Wix AI chatbot'),
      title: t('Answers on every page of your Wix site.'),
      accent: t('From one line of code.'),
      lead: t(
        'Paste one snippet into your site’s Custom code and a chat bubble trained on your own content answers visitors, captures leads and books appointments.',
      ),
      facts: [
        t('Works on the Free plan'),
        t('One snippet, every page'),
        t('Your colors and icon'),
      ],
      description: t(
        'Example: a visitor on a florist’s Wix site asks about next-day delivery. The agent answers from the delivery policy and shows a Shop bouquets button.',
      ),
      demo: (
        <SiteChatDemo
          brand={BRAND}
          url='bloomandstem.wixsite.com/flowers'
          site={{
            headline: t('Fresh flowers, delivered tomorrow.'),
            picture:
              'bg-[linear-gradient(135deg,#fda4af_0%,#e11d48_55%,#881337_100%)]',
            accent: 'text-[#e11d48] dark:text-[#fda4af]',
          }}
          lines={[
            {
              from: 'visitor',
              text: t('Can you deliver a bouquet tomorrow?'),
              at: 700,
            },
            {
              from: 'agent',
              content: t(
                'Yes! Order by 18:00 today for delivery anywhere in the city tomorrow.',
              ),
              typingAt: 1200,
              at: 2100,
            },
            {
              from: 'agent',
              content: <ChatButton brand={BRAND} label={t('Shop bouquets')} />,
              at: 2700,
            },
          ]}
        />
      ),
      wide: true,
      steps: [],
    },
    jobs: {
      title: t('Turns visitors into customers.'),
      tail: t('Even when you’re offline.'),
      lead: t(
        'Visitors leave when they can’t find an answer. Your agent knows your pages, prices and policies, and answers in seconds, on every page.',
      ),
      items: [
        {
          icon: ChatAlt2Icon,
          title: t('Answers questions'),
          detail: t(
            'Opening hours, prices, services and policies, straight from your website and documents.',
          ),
        },
        {
          icon: IdentificationIcon,
          title: t('Captures leads'),
          detail: t(
            'Collects names, emails and anything else you ask for, one question at a time, and saves them to Leads.',
          ),
        },
        {
          icon: CursorClickIcon,
          title: t('Shows buttons'),
          detail: t(
            'Sends visitors to booking, pricing or checkout pages with a button in the chat.',
          ),
        },
        {
          icon: CalendarIcon,
          title: t('Books appointments'),
          detail: t(
            'Checks when you’re free and books the time in your Google Calendar.',
          ),
        },
        {
          icon: GlobeAltIcon,
          title: t('Speaks their language'),
          detail: t(
            'Replies in the language each customer writes in, from the same knowledge.',
          ),
        },
        {
          icon: MicrophoneIcon,
          title: t('Talks out loud'),
          detail: t(
            'On Unlimited 200 and up, visitors can talk to it by voice right in the chat.',
          ),
        },
      ],
    },
    setup: {
      title: t('Add it in Custom code.'),
      tail: t('One snippet, every page.'),
      steps: [
        {
          title: t('Copy the snippet'),
          detail: t(
            'In Easy-Peasy.AI, open your agent, go to Channels, then Chat bubble, click Manage and copy the install snippet.',
          ),
        },
        {
          title: t('Add it in Custom code'),
          detail: t(
            'In your Wix dashboard, open Settings, then Custom code, click Add Code and paste it. Choose All pages, Load code once and Body - end.',
          ),
        },
        {
          title: t('Check your live site'),
          detail: t(
            'Click Apply and open your published site. The chat bubble appears on every page.',
          ),
        },
      ],
      guide: 'https://easy-peasy.ai/docs/agents/website-platforms#wix',
      guideLabel: t('Read the Wix setup guide'),
      description: t(
        'Wix’s Add Custom Code window with the install snippet pasted in, set to all pages and the end of the body, and a note that the chat is live on every page.',
      ),
      visual: (
        <CodePanel
          platform={
            <>
              <FaWix className='h-6 w-6 text-[#0f0f0f] dark:text-white' />
              <span className='text-sm font-semibold'>Wix</span>
            </>
          }
          title={t('Add Custom Code')}
          fields={[
            {
              label: t('Add Code to Pages'),
              value: t('All pages, load code once'),
            },
            { label: t('Place Code in'), value: t('Body - end') },
          ]}
          codeLabel={t('Paste the code snippet here')}
          primary={t('Apply')}
          primaryClass='bg-[#116dff] text-white'
          note={t('Shows on your published site')}
        />
      ),
    },
    spotlight: {
      title: t('Turn questions into leads.'),
      tail: t('Right in the chat.'),
      lead: t(
        'Turn on the lead form and it appears after the agent’s first answer, or let the agent ask for details in the conversation. Every lead lands in Leads, and you can get an email for each one.',
      ),
      points: [
        t('A short form after the first answer'),
        t('Or the agent asks, one question at a time'),
        t('An email for every new lead'),
        t('Lead form and alerts on paid plans'),
      ],
      description: t(
        'The website chat showing the lead form under the agent’s first answer, next to the Leads list with the latest contacts.',
      ),
      visual: (
        <LeadCapture
          brand={BRAND}
          question={t('Can you deliver a bouquet tomorrow?')}
          answer={t(
            'Yes! Order by 18:00 today for delivery anywhere in the city tomorrow.',
          )}
          leads={[
            {
              name: 'Sofia Marin',
              email: 'sofia@example.com',
              date: t('Today'),
            },
            {
              name: 'Liam Walsh',
              email: 'liam@example.com',
              date: t('Yesterday'),
            },
            {
              name: 'Hana Ito',
              email: 'hana@example.com',
              date: t('2 days ago'),
            },
          ]}
        />
      ),
    },
    rules: {
      title: t('Good to know.'),
      tail: t('Wix, in plain words.'),
      items: [
        {
          icon: GiftIcon,
          title: t('Free plan included'),
          detail: t(
            'The website chat works on every plan, including Free. Actions, such as buttons and bookings, need a paid plan.',
          ),
        },
        {
          icon: ExclamationCircleIcon,
          title: t('Wix plans'),
          detail: t(
            'Custom code needs a Wix plan that allows it. Without it, add the chat inside a page with an Embed HTML element.',
          ),
        },
        {
          icon: EyeIcon,
          title: t('On your published site'),
          detail: t(
            'Custom code shows on your published site. Open it there to see the chat bubble.',
          ),
        },
        {
          icon: LightningBoltIcon,
          title: t('Doesn’t slow your site'),
          detail: t(
            'The bubble shows right away, and the chat itself loads once the page has settled.',
          ),
        },
        {
          icon: RefreshIcon,
          title: t('Changes go live on their own'),
          detail: t(
            'Change the bubble’s look or greeting in Appearance and your site updates within about a minute, with no new code.',
          ),
        },
        {
          icon: MicrophoneIcon,
          title: t('Voice on Unlimited 200 and up'),
          detail: t(
            'Visitors can talk to your agent by voice on Unlimited 200 and up. On other plans, try voice in the Playground.',
          ),
        },
      ],
    },
    faqs: [
      {
        question: t('How do I add an AI chatbot to my Wix site?'),
        answer: t(
          'Copy your agent’s install snippet, then in your Wix dashboard open Settings, Custom code, click Add Code, paste it, choose All pages and Body - end, and click Apply.',
        ),
      },
      {
        question: t('What if I don’t see Custom code?'),
        answer: t(
          'Your Wix plan may not allow it. Use the Wix Editor instead: add an Embed HTML element, paste the iframe code and publish.',
        ),
      },
      ...websiteFaqs,
    ],
    finalTitle: t('Your Wix site could be answering by lunchtime.'),
    finalExchange: {
      question: t('Do you have sunflowers this week?'),
      answer: t('Yes! They’re in our Summer Sun bouquet.'),
    },
  };
}
